import React from 'react';
import { t } from '../../translation';
import { buildAuthHeaders } from '../../helpers';

// Configuration constants
const GALLERY_COLUMNS = 4; // Number of columns in the grid
const GALLERY_ROWS = 5;    // Number of rows visible at once

interface Photo {
    id: string;
    url: string;
    thumbnail?: string;
    name?: string;
    cameraId?: string;
}

interface Damage {
    id: string;
    name: string;
}

interface ImageGalleryProps {
    isOpen: boolean;
    onClose: () => void;
    requestId: string;
    apiBaseUrl: string;
    apiStage: string;
    apiPrefix: string;
    damages?: Damage[]; // List of available damages
    mode?: 'normal' | 'selector'; // Normal mode or selector mode for assigning photos
    onPhotosSelected?: (photoIds: string[]) => void; // Callback when photos are selected in selector mode
    damagePhotos?: string[]; // Array of photo IDs to filter/display (for viewing damage photos)
}

interface ImageGalleryState {
    photos: Photo[];
    loading: boolean;
    error: string | null;
    enlargedIndex: number | null;
    selectedPhotos: Set<string>; // For multi-select
    hoveredPhotoId: string | null;
    showMenuForPhoto: string | null;
    isSelectionModeActive: boolean; // Selection mode state
    showDamageAssignmentModal: boolean; // Modal to assign photos to damage
}

class ImageGallery extends React.Component<ImageGalleryProps, ImageGalleryState> {
    constructor(props: ImageGalleryProps) {
        super(props);
        this.state = {
            photos: [],
            loading: false,
            error: null,
            enlargedIndex: null,
            selectedPhotos: new Set(),
            hoveredPhotoId: null,
            showMenuForPhoto: null,
            isSelectionModeActive: false,
            showDamageAssignmentModal: false
        };
    }

    componentDidMount() {
        if (this.props.isOpen) {
            this.loadPhotos();
            this.disableBodyScroll();
        }
    }

    componentDidUpdate(prevProps: ImageGalleryProps) {
        if (this.props.isOpen && !prevProps.isOpen) {
            this.loadPhotos();
            this.disableBodyScroll();
        } else if (!this.props.isOpen && prevProps.isOpen) {
            this.enableBodyScroll();
        }
    }

    loadPhotos = async () => {
        if (!this.props.requestId) {
            this.setState({ error: t('gallery.noRequestId') });
            return;
        }

        this.setState({ loading: true, error: null });

        try {
            const url = `${this.props.apiBaseUrl}/${this.props.apiStage}/${this.props.apiPrefix}/requests/${this.props.requestId}/photos?type=map`;
            console.log('Fetching photos from:', url);

            const response = await fetch(url, { headers: buildAuthHeaders() });
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            console.log('API Response:', data);
            
            // Transform the response to our Photo interface
            // API returns: { media: [ { IdMedia: "...", thumbnails: [...], frames: [...] } ] }
            const photos: Photo[] = [];
            
            if (data.media && Array.isArray(data.media)) {
                data.media.forEach((mediaItem: any) => {
                    if (mediaItem.thumbnails && Array.isArray(mediaItem.thumbnails)) {
                        // Create a map of frames by frameNum for easy lookup
                        const framesMap = new Map();
                        if (mediaItem.frames && Array.isArray(mediaItem.frames)) {
                            mediaItem.frames.forEach((frame: any) => {
                                framesMap.set(frame.frameNum, frame);
                            });
                        }
                        
                        mediaItem.thumbnails.forEach((thumb: any, thumbIndex: number) => {
                            // Get corresponding full-size frame by frameNum
                            const frame = framesMap.get(thumb.frameNum);
                            
                            photos.push({
                                id: `${mediaItem.IdMedia}-${thumbIndex}`,
                                thumbnail: thumb.url, // Use thumbnail URL from API
                                url: frame ? frame.url : thumb.url, // Use frame URL from API, fallback to thumbnail
                                name: thumb.frameName || `Photo ${photos.length + 1}`
                            });
                        });
                    }
                });
            }

            console.log('Parsed photos:', photos);

            // Resolve the real cameraId for each photo using viewer camera data
            for (const photo of photos) {
                if (photo.name) {
                    photo.cameraId = this.resolveCameraId(photo.name);
                }
            }
            
            // Filter photos only in 'normal' mode (viewing damage photos)
            // In 'selector' mode, show ALL photos but pre-select the ones in damagePhotos
            let filteredPhotos = photos;
            if (this.props.mode !== 'selector' && this.props.damagePhotos && this.props.damagePhotos.length > 0) {
                filteredPhotos = photos.filter(photo => this.props.damagePhotos.includes(photo.id));
                console.log('Filtered photos for damage (normal mode):', filteredPhotos);
            }
            
            // Pre-select photos in selector mode AFTER photos are loaded
            if (this.props.mode === 'selector' && this.props.damagePhotos && this.props.damagePhotos.length > 0) {
                console.log('Pre-selecting photos:', this.props.damagePhotos);
                this.setState({ 
                    photos: filteredPhotos, 
                    loading: false,
                    isSelectionModeActive: true,
                    selectedPhotos: new Set(this.props.damagePhotos)
                });
            } else {
                this.setState({ 
                    photos: filteredPhotos, 
                    loading: false,
                    isSelectionModeActive: false,
                    selectedPhotos: new Set()
                });
            }
        } catch (error) {
            console.error('Error loading photos:', error);
            this.setState({ 
                error: t('gallery.loadError'), 
                loading: false 
            });
        }
    };

    handleClose = () => {
        this.setState({ 
            enlargedIndex: null,
            selectedPhotos: new Set(),
            showMenuForPhoto: null,
            isSelectionModeActive: false
        });
        this.props.onClose();
    };

    handlePhotoClick = (index: number, event: React.MouseEvent) => {
        // Don't open enlarged view if clicking on the menu button or checkbox
        if ((event.target as HTMLElement).closest('.gallery-image-menu-button') || 
            (event.target as HTMLElement).closest('.gallery-checkbox-label')) {
            return;
        }
        
        // If selection mode is active, toggle selection instead of opening enlarged view
        if (this.state.isSelectionModeActive) {
            const photo = this.state.photos[index];
            if (photo) {
                this.togglePhotoSelection(photo.id);
            }
            return;
        }
        
        this.setState({ enlargedIndex: index });
    };

    handleCloseEnlarged = () => {
        this.setState({ enlargedIndex: null });
    };

    handlePrevious = () => {
        if (this.state.enlargedIndex === null) return;
        const newIndex = this.state.enlargedIndex > 0 
            ? this.state.enlargedIndex - 1 
            : this.state.photos.length - 1;
        this.setState({ enlargedIndex: newIndex });
    };

    handleNext = () => {
        if (this.state.enlargedIndex === null) return;
        const newIndex = this.state.enlargedIndex < this.state.photos.length - 1 
            ? this.state.enlargedIndex + 1 
            : 0;
        this.setState({ enlargedIndex: newIndex });
    };

    /**
     * Resolve the real cameraId for a given frameName/imageName by looking up
     * the camera data already available in window.viewer.
     * Falls back to the original frameName if no mapping is found.
     */
    private resolveCameraId(frameName: string): string {
        try {
            const viewer = (window as any).viewer;
            if (!viewer) {
                console.warn('[ImageGallery] resolveCameraId: window.viewer not available, falling back to frameName:', frameName);
                return frameName;
            }

            const photoIds: string[] = viewer.cameraModelPhotoIds;
            const cameraIds: string[] = viewer.cameraModelCameraIds;

            if (!Array.isArray(photoIds) || !Array.isArray(cameraIds)) {
                console.warn('[ImageGallery] resolveCameraId: camera arrays not available, falling back to frameName:', frameName);
                return frameName;
            }

            // Extract the numeric part from frameName for flexible matching.
            // frameName from photos/map API may have a "thumb_" prefix
            // (e.g. "thumb_000092.jpg") while imageName from cameras API
            // does not (e.g. "000092.jpg"). We try exact match first,
            // then stripped-prefix match, then numeric-part match.
            const strippedFrame = frameName.replace(/^thumb_/i, '');

            for (let i = 0; i < photoIds.length; i++) {
                if (!cameraIds[i]) continue;
                const pid = photoIds[i];
                if (pid === frameName || pid === strippedFrame) {
                    console.log(`[ImageGallery] resolveCameraId: ${frameName} → ${cameraIds[i]} (matched via ${pid === frameName ? 'exact' : 'stripped prefix'})`);
                    return cameraIds[i];
                }
            }

            // Last resort: match by numeric portion (e.g. "000092")
            const frameNum = frameName.match(/(\d{3,})/)?.[1];
            if (frameNum) {
                for (let i = 0; i < photoIds.length; i++) {
                    if (!cameraIds[i]) continue;
                    const pidNum = photoIds[i].match(/(\d{3,})/)?.[1];
                    if (pidNum && pidNum === frameNum) {
                        console.log(`[ImageGallery] resolveCameraId: ${frameName} → ${cameraIds[i]} (matched via numeric ${frameNum})`);
                        return cameraIds[i];
                    }
                }
            }

            console.warn(`[ImageGallery] resolveCameraId: no match found for frameName "${frameName}", photoIds sample:`, photoIds.slice(0, 3));
        } catch (e) {
            console.error('[ImageGallery] resolveCameraId error:', e);
        }
        return frameName;
    }

    /**
     * Collect damage markers for a given camera ID by fetching the damages API.
     * Returns an array of markers with color, position and size in natural image pixels.
     */
    private async getMarkersForPhoto(cameraId: string): Promise<Array<{
        centerX: number; centerY: number; width: number; height: number; color: string;
    }>> {
        if (!cameraId || !this.props.requestId) return [];

        const { apiBaseUrl, apiStage, apiPrefix, requestId } = this.props;
        const damagesUrl = `${apiBaseUrl}/${apiStage}/${apiPrefix}/damages/${encodeURIComponent(requestId)}`;

        try {
            const response = await fetch(damagesUrl, {
                method: 'GET',
                headers: buildAuthHeaders()
            });

            if (!response.ok) return [];

            const data = await response.json();
            const damageList: any[] = Array.isArray(data?.damageList) ? data.damageList : [];

            // Determine the currently selected damage ID from the viewer (for color assignment)
            const selectedDamageIds: Set<string> = new Set();
            if (window.viewer?.selectedCameraDamageIds instanceof Set) {
                window.viewer.selectedCameraDamageIds.forEach((id: string) => selectedDamageIds.add(id));
            }

            const markers: Array<{
                centerX: number; centerY: number; width: number; height: number; color: string;
            }> = [];

            for (const entry of damageList) {
                if (
                    entry &&
                    (entry.markerMetadata === true || entry.name === '__camera_markers__') &&
                    entry.cameraId === cameraId &&
                    Array.isArray(entry.imageMarkers)
                ) {
                    const isSelected = selectedDamageIds.has(entry.damageId);
                    const color = isSelected ? '#26a89e' : 'rgba(255, 89, 122, 0.95)';

                    for (const m of entry.imageMarkers) {
                        if (typeof m.centerX === 'number' && typeof m.centerY === 'number' &&
                            typeof m.width === 'number' && typeof m.height === 'number') {
                            markers.push({
                                centerX: m.centerX,
                                centerY: m.centerY,
                                width: m.width,
                                height: m.height,
                                color
                            });
                        }
                    }
                }
            }

            return markers;
        } catch (err) {
            console.warn('[ImageGallery] Unable to fetch damage markers for download.', err);
            return [];
        }
    }

    /**
     * Download the original image as a blob (no markers).
     */
    private async downloadOriginalImage(imageUrl: string, filename: string): Promise<void> {
        const response = await fetch(imageUrl);
        if (!response.ok) {
            throw new Error(`Unable to download image. Status: ${response.status}`);
        }
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        const extension = blob.type.includes('png') ? 'png' : 'jpg';
        link.href = blobUrl;
        link.download = `${filename}.${extension}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
    }

    downloadEnlargedPhoto = async () => {
        const { enlargedIndex, photos } = this.state;
        if (enlargedIndex === null) {
            return;
        }

        const photo = photos[enlargedIndex];
        if (!photo?.url) {
            return;
        }

        const safeName = (photo.name || `photo-${enlargedIndex + 1}`).replace(/[^a-zA-Z0-9-_]/g, '_');

        try {
            // Resolve cameraId at download time (in case it wasn't available during loadPhotos)
            const resolvedCameraId = this.resolveCameraId(photo.name || '');
            const cameraIdForMarkers = resolvedCameraId !== (photo.name || '') ? resolvedCameraId : (photo.cameraId || photo.name || '');
            console.log(`[ImageGallery] downloadEnlargedPhoto: photo.name="${photo.name}", photo.cameraId="${photo.cameraId}", resolvedNow="${resolvedCameraId}", using="${cameraIdForMarkers}"`);
            const markers = await this.getMarkersForPhoto(cameraIdForMarkers);
            console.log(`[ImageGallery] downloadEnlargedPhoto: got ${markers.length} markers`);

            // If no markers, download original image directly (Req 3.5)
            if (markers.length === 0) {
                await this.downloadOriginalImage(photo.url, safeName);
                return;
            }

            // Load image with CORS for canvas composition (Req 3.1)
            const img = await new Promise<HTMLImageElement>((resolve, reject) => {
                const image = new Image();
                image.crossOrigin = 'anonymous';
                image.onload = () => resolve(image);
                image.onerror = () => reject(new Error('Image load failed'));
                image.src = photo.url;
            });

            const { naturalWidth, naturalHeight } = img;

            // Create canvas at full resolution (Req 3.1)
            const canvas = document.createElement('canvas');
            canvas.width = naturalWidth;
            canvas.height = naturalHeight;

            const ctx = canvas.getContext('2d');
            if (!ctx) {
                throw new Error('Could not create canvas context');
            }

            // Draw base image (Req 3.2)
            ctx.drawImage(img, 0, 0, naturalWidth, naturalHeight);

            // Draw each marker as strokeRect (Req 3.3)
            const borderWidth = Math.max(3, Math.round(Math.max(naturalWidth, naturalHeight) / 500));
            for (const marker of markers) {
                const x = marker.centerX - marker.width * 0.5;
                const y = marker.centerY - marker.height * 0.5;
                ctx.strokeStyle = marker.color;
                ctx.lineWidth = borderWidth;
                ctx.strokeRect(x, y, marker.width, marker.height);
            }

            // Convert to PNG blob and trigger download (Req 3.4, 3.6)
            const downloadName = `${safeName}_damage.png`;

            const blob = await new Promise<Blob | null>((resolve) => {
                canvas.toBlob((b) => resolve(b), 'image/png');
            });

            if (blob) {
                const blobUrl = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = blobUrl;
                link.download = downloadName;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(blobUrl);
                return;
            }

            // Fallback: toBlob failed, download original image without markers (Req 5.2)
            await this.downloadOriginalImage(photo.url, safeName);
        } catch (error) {
            console.error('Unable to download enlarged photo locally.', error);
            alert(t('gallery.downloadError'));
        }
    };

    handleKeyDown = (event: KeyboardEvent) => {
        if (this.state.enlargedIndex !== null) {
            if (event.key === 'ArrowLeft') {
                this.handlePrevious();
            } else if (event.key === 'ArrowRight') {
                this.handleNext();
            } else if (event.key === 'Escape') {
                this.handleCloseEnlarged();
            }
        }
    };

    componentWillUnmount() {
        document.removeEventListener('keydown', this.handleKeyDown);
        this.enableBodyScroll();
    }

    disableBodyScroll = () => {
        document.body.style.overflow = 'hidden';
    };

    enableBodyScroll = () => {
        document.body.style.overflow = '';
    };

    handleWheelEvent = (e: React.WheelEvent) => {
        // Stop wheel event from propagating to the viewer canvas
        e.stopPropagation();
    };

    togglePhotoSelection = (photoId: string) => {
        this.setState(prevState => {
            const newSelectedPhotos = new Set(prevState.selectedPhotos);
            if (newSelectedPhotos.has(photoId)) {
                newSelectedPhotos.delete(photoId);
            } else {
                newSelectedPhotos.add(photoId);
            }
            
            // Activate selection mode when first photo is selected
            const isSelectionModeActive = newSelectedPhotos.size > 0;
            
            return { 
                selectedPhotos: newSelectedPhotos,
                isSelectionModeActive,
                showMenuForPhoto: null as string | null // Close menu when selecting
            };
        });
    };

    toggleMenu = (photoId: string, event: React.MouseEvent) => {
        event.stopPropagation();
        this.setState(prevState => ({
            showMenuForPhoto: prevState.showMenuForPhoto === photoId ? null : photoId
        }));
    };

    handleAssignToDamage = (damageId: string) => {
        if (!window.viewer) return;

        const selectedPhotoIds = Array.from(this.state.selectedPhotos);
        console.log('Assigning photos to damage:', damageId, selectedPhotoIds);

        // Find the damage index by ID
        const allSpheres = window.viewer.getDamageSphereRenderComponents?.() || [];
        const damageIndex = parseInt(damageId.replace('dmg-', '')) - 1;

        if (damageIndex >= 0 && damageIndex < allSpheres.length) {
            // Get existing photos for this damage
            const existingPhotos = window.viewer.getPhotosForDamage(damageIndex);
            
            // Add new photos (avoiding duplicates)
            const newPhotos = [...existingPhotos];
            selectedPhotoIds.forEach(photoId => {
                if (!newPhotos.includes(photoId)) {
                    newPhotos.push(photoId);
                }
            });

            // Update the damage photos (additive)
            window.viewer.photosPointer[damageIndex] = newPhotos;
            console.log(`Photos assigned to damage ${damageIndex}:`, newPhotos);
        }

        this.setState({ 
            showMenuForPhoto: null, 
            selectedPhotos: new Set(),
            isSelectionModeActive: false,
            showDamageAssignmentModal: false
        });
    };

    handleCancelSelection = () => {
        this.setState({ 
            selectedPhotos: new Set(),
            isSelectionModeActive: false,
            showMenuForPhoto: null
        });
    };

    handleAddToAssignment = () => {
        const selectedPhotoIds = Array.from(this.state.selectedPhotos);
        console.log('handleAddToAssignment - mode:', this.props.mode, 'selectedPhotoIds:', selectedPhotoIds);
        
        // If in selector mode, call the callback
        if (this.props.mode === 'selector' && this.props.onPhotosSelected) {
            console.log('Calling onPhotosSelected with:', selectedPhotoIds);
            this.props.onPhotosSelected(selectedPhotoIds);
            this.handleClose(); // Close the gallery after selection
        } else {
            // In normal mode, show damage assignment modal
            console.log('Add to assignment clicked. Selected photos:', selectedPhotoIds);
            this.setState({ showDamageAssignmentModal: true });
        }
    };

    renderEnlargedView() {
        const { enlargedIndex, photos } = this.state;
        if (enlargedIndex === null) return null;

        const photo = photos[enlargedIndex];
        if (!photo) return null;

        return (
            <div className="gallery-enlarged-overlay" onWheel={this.handleWheelEvent}>
                <div className="gallery-enlarged-container" onWheel={this.handleWheelEvent}>
                    <div className="gallery-enlarged-media">
                        <div className="gallery-enlarged-image-frame">
                            <button 
                                className="gallery-enlarged-action gallery-enlarged-download" 
                                onClick={this.downloadEnlargedPhoto}
                                title={t('gallery.download')}
                            >
                                ↓
                            </button>

                            <button 
                                className="gallery-enlarged-action gallery-enlarged-close" 
                                onClick={this.handleCloseEnlarged}
                                title={t('gallery.close')}
                            >
                                ×
                            </button>

                            <button 
                                className="gallery-enlarged-nav gallery-enlarged-prev" 
                                onClick={this.handlePrevious}
                                title={t('gallery.previous')}
                            >
                                ‹
                            </button>

                            <img 
                                src={photo.url} 
                                alt={photo.name || `Photo ${enlargedIndex + 1}`}
                                className="gallery-enlarged-image"
                            />

                            <button 
                                className="gallery-enlarged-nav gallery-enlarged-next" 
                                onClick={this.handleNext}
                                title={t('gallery.next')}
                            >
                                ›
                            </button>
                        </div>
                    </div>

                    <div className="gallery-enlarged-info">
                        {photo.name || `${t('gallery.photo')} ${enlargedIndex + 1} ${t('gallery.of')} ${photos.length}`}
                    </div>
                </div>
            </div>
        );
    }

    toggleSelectionMode = (photoId?: string) => {
        this.setState(prevState => {
            const newSelectedPhotos = new Set<string>();
            
            // If activating selection mode and photoId is provided, select that photo
            if (!prevState.isSelectionModeActive && photoId) {
                newSelectedPhotos.add(photoId);
            }
            
            return {
                isSelectionModeActive: !prevState.isSelectionModeActive,
                selectedPhotos: newSelectedPhotos,
                showMenuForPhoto: null as string | null
            };
        });
    };

    renderMenu(photoId: string) {
        return (
            <div className="gallery-image-menu">
                <div 
                    className="gallery-image-menu-item gallery-image-menu-item-selectable"
                    onClick={(e) => {
                        e.stopPropagation();
                        this.toggleSelectionMode(photoId);
                    }}
                >
                    <label className="gallery-checkbox-label">
                        <input 
                            type="checkbox"
                            checked={this.state.isSelectionModeActive}
                            onChange={() => {}} // Handled by parent div onClick
                            readOnly
                        />
                        <span className="gallery-checkbox-custom"></span>
                        {t('gallery.selectForAssignment')}
                    </label>
                </div>
            </div>
        );
    }

    renderDamageAssignmentModal() {
        if (!this.state.showDamageAssignmentModal || !window.viewer) return null;

        const allSpheres = window.viewer.getDamageSphereRenderComponents?.() || [];
        const damages = allSpheres.map((sphere: any, index: number) => ({
            id: `dmg-${String(index + 1).padStart(4, '0')}`,
            name: sphere.entity.name || `Damage ${index + 1}`
        }));

        return (
            <div className="gallery-modal-overlay" onClick={() => this.setState({ showDamageAssignmentModal: false })}>
                <div className="gallery-modal-content" onClick={(e) => e.stopPropagation()}>
                    <h3>{t('gallery.assignToDamage')}</h3>
                    <div className="gallery-modal-damage-list">
                        {damages.length > 0 ? (
                            damages.map(damage => (
                                <button
                                    key={damage.id}
                                    className="gallery-modal-damage-button"
                                    onClick={() => this.handleAssignToDamage(damage.id)}
                                >
                                    {damage.name}
                                </button>
                            ))
                        ) : (
                            <p>{t('gallery.noDamages')}</p>
                        )}
                    </div>
                    <button 
                        className="gallery-modal-close-button"
                        onClick={() => this.setState({ showDamageAssignmentModal: false })}
                    >
                        {t('gallery.cancel')}
                    </button>
                </div>
            </div>
        );
    }

    renderOldMenu(photoId: string) {
        const { damages = [] } = this.props;
        const hasDamages = damages.length > 0;

        return (
            <div className="gallery-image-menu">
                <div 
                    className="gallery-image-menu-item gallery-image-menu-item-selectable"
                    onClick={() => this.togglePhotoSelection(photoId)}
                >
                    <label className="gallery-checkbox-label">
                        <input 
                            type="checkbox"
                            checked={this.state.selectedPhotos.has(photoId)}
                            onChange={() => {}} // Handled by parent div onClick
                            readOnly
                        />
                        <span className="gallery-checkbox-custom"></span>
                        {t('gallery.selectForAssignment')}
                    </label>
                </div>
                
                {hasDamages ? (
                    <>
                        <div className="gallery-image-menu-divider"></div>
                        <div className="gallery-image-menu-subtitle">
                            {t('gallery.assignToDamage')}
                        </div>
                        {damages.map(damage => (
                            <div 
                                key={damage.id}
                                className="gallery-image-menu-item"
                                onClick={() => this.handleAssignToDamage(damage.id)}
                            >
                                {damage.name}
                            </div>
                        ))}
                    </>
                ) : (
                    <>
                        <div className="gallery-image-menu-divider"></div>
                        <div className="gallery-image-menu-item gallery-image-menu-item-disabled">
                            {t('gallery.noDamages')}
                        </div>
                    </>
                )}
            </div>
        );
    }

    renderGrid() {
        const { photos, selectedPhotos, hoveredPhotoId, showMenuForPhoto } = this.state;

        if (photos.length === 0 && !this.state.loading) {
            return (
                <div className="gallery-empty">
                    {t('gallery.noPhotos')}
                </div>
            );
        }

        return (
            <div className="gallery-grid" style={{
                gridTemplateColumns: `repeat(${GALLERY_COLUMNS}, 1fr)`
            }}>
                {photos.map((photo, index) => {
                    const isSelected = selectedPhotos.has(photo.id);
                    const isHovered = hoveredPhotoId === photo.id;
                    const showMenu = showMenuForPhoto === photo.id;

                    return (
                        <div 
                            key={photo.id}
                            className={`gallery-image-wrapper ${isSelected ? 'selected' : ''} ${isHovered ? 'hovered' : ''}`}
                            onMouseEnter={() => this.setState({ hoveredPhotoId: photo.id })}
                            onMouseLeave={() => this.setState({ hoveredPhotoId: null })}
                            onClick={(e) => this.handlePhotoClick(index, e)}
                        >
                            <img 
                                src={photo.thumbnail || photo.url} 
                                alt={photo.name || `Photo ${index + 1}`}
                                className="gallery-image"
                            />
                            
                            {isSelected && (
                                <div className="gallery-image-selected-badge">
                                    ✓
                                </div>
                            )}

                            {(isHovered || showMenu) && (
                                <button 
                                    className="gallery-image-menu-button"
                                    onClick={(e) => this.toggleMenu(photo.id, e)}
                                    title={t('gallery.options')}
                                >
                                    ⋮
                                </button>
                            )}

                            {showMenu && this.renderMenu(photo.id)}
                        </div>
                    );
                })}
            </div>
        );
    }

    render() {
        const { isOpen, mode, damagePhotos } = this.props;
        const { loading, error } = this.state;

        if (!isOpen) return null;

        // Add keyboard event listener when component is mounted and gallery is open
        if (this.state.enlargedIndex !== null) {
            document.addEventListener('keydown', this.handleKeyDown as any);
        } else {
            document.removeEventListener('keydown', this.handleKeyDown as any);
        }
        
        // Determine title based on mode
        let galleryTitle = t('gallery.title');
        if (mode === 'selector') {
            galleryTitle = t('gallery.selectPhotos');
        } else if (damagePhotos && damagePhotos.length > 0) {
            galleryTitle = t('gallery.damagePhotos');
        }

        return (
            <>
                <div className="gallery-overlay" onWheel={this.handleWheelEvent}>
                    <div className="gallery-container" onWheel={this.handleWheelEvent}>
                        <div className="gallery-header">
                            <h2 className="gallery-title">{galleryTitle}</h2>
                            <button 
                                className="gallery-close-button" 
                                onClick={this.handleClose}
                                title={t('gallery.close')}
                            >
                                ×
                            </button>
                        </div>

                        <div className="gallery-content">
                            {loading && (
                                <div className="gallery-loading">
                                    <div className="gallery-spinner"></div>
                                    <div>{t('gallery.loading')}</div>
                                </div>
                            )}

                            {error && (
                                <div className="gallery-error">
                                    {error}
                                </div>
                            )}

                            {!loading && !error && this.renderGrid()}
                        </div>

                        {this.state.selectedPhotos.size > 0 && (
                            <div className="gallery-footer">
                                <div className="gallery-footer-left">
                                    {t('gallery.selectedCount').replace('{count}', this.state.selectedPhotos.size.toString())}
                                </div>
                                <div className="gallery-footer-right">
                                    <button 
                                        className="gallery-footer-button gallery-footer-button-cancel"
                                        onClick={this.handleCancelSelection}
                                    >
                                        {t('gallery.cancel')}
                                    </button>
                                    <button 
                                        className="gallery-footer-button gallery-footer-button-add"
                                        onClick={this.handleAddToAssignment}
                                    >
                                        {t('gallery.add')}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {this.renderEnlargedView()}
                {this.renderDamageAssignmentModal()}
            </>
        );
    }
}

export default ImageGallery;
